/**
 * @author Красильников Даниил <dkrasilnikov@gmail.com>
 * @link https://t.me/krasilneg
 * 
 * @author Колесник Данил <90.kalias.3@gmail.com>
 * @link https://t.me/kalias_90
 */

import React, { HTMLAttributes, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import ToolbarButton, { PlainToolbarButton } from './ToolbarButton.js';
import ClippingIcon from '../icons/Clipping.js';
import PlusIcon from '../../icons/Plus.js';
import PlaneIcon from '../icons/Plane.js';
import MinusIcon from '../../icons/Minus.js';
import MoveIcon from '@/components/icons/Move';
import RotateIcon from '@/components/icons/Rotate';
import FlipIcon from '../icons/Flip.js';
import CrossIcon from '@/components/icons/Cross';
import ConjunctionIcon from '@/components/icons/Conjunction';
import NotConjunctionIcon from '@/components/icons/NotConjunction';

import { Action, AddPlaneParams } from '@bimdata/viewer-react';
import { ClippingPlane, ClippingPlaneOrientation } from '@bimdata/definitions/types';
import t from '@/utils/translate';

const Root = styled('div')({
  position: 'relative',
});

const FlyingPanel = styled('div')({
  position: 'absolute',
  bottom: '50px',
  left: '-13px',
  padding: '6px',
  minWidth: '56px',
  minHeight: '56px',
  gap: '6px 0px',
  flexDirection: 'row',
  flexWrap: 'wrap',
  display: 'flex'
});

const FlyingPanelCell = styled('div')({
  height: '56px',
  padding: '6px',
  gap: '4px',
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
});

const Toolgroup = styled(FlyingPanelCell)({
  padding: '5px',
  borderRadius: '6px',
  border: '1px solid #E7E7E7',
  boxShadow: '0px 0px 10px 0px #0000001A',
  background: '#FFFFFF',
  display: 'flex',
});

const OrientationSelectorGroup = styled(Toolgroup)({
  position: 'absolute',
  top: '5px',
  left: '5px',
});

type OrigentationSelectorProps = {
  addPlane: (d: Omit<AddPlaneParams, 'id'>) => void
} & HTMLAttributes<HTMLDivElement>;

const OrientationSelector = (
  { addPlane, ...props }: OrigentationSelectorProps
) => {
  return <OrientationSelectorGroup {...props}>
    <ToolbarButton
      hint={t('viewer.action.addXCut')}
      onClick={() => addPlane({ orientation: 'x' })}
    >
      <PlaneIcon fill='#FF0000' />
    </ToolbarButton>
    <ToolbarButton
      hint={t('viewer.action.addYCut')}
      onClick={() => addPlane({ orientation: 'z' })}
    >
      <PlaneIcon
        rotation={180}
        fill='#00FF00'
      />
    </ToolbarButton>
    <ToolbarButton
      hint={t('viewer.action.addZCut')}
      onClick={() => addPlane({ orientation: 'y' })}
    >
      <PlaneIcon
        rotation={270}
        fill='#0000FF'
      />
    </ToolbarButton>
  </OrientationSelectorGroup>;
}

const FlyingButton = styled(ToolbarButton)(({ color = '#091e4333' }) => ({
  color: '#091e4333',
  '&:disabled': {
    background: 'transparent',
    opacity: '20%',
  },
  '&:not(:disabled):hover': {
    background: 'transparent',
    color,
  },
  '&:not(:disabled):active': {
    borderWidth: 0,
    padding: '10px',
    boxShadow: 'none',
  },
}));

function iconRotationByPlaneOrientation(o: ClippingPlaneOrientation): number {
  switch (o) {
    case 'x': return 0;
    case 'y': return 270;
    case 'z': return 180;
  }
}

function switcherNamePrefix(state: boolean): string {
  return state ? t('action.off') : t('action.on');
}

const UnselectedPlane = styled('div')({
  display: 'flex',
  opacity: '30%',
  '&:hover': {
    opacity: '70%',
  }
});

type PlaneToolgroupType = {
  selected: boolean,
  perform: (type: Action, data?: any) => void,
  flipped: boolean,
  onFlip: (id: string) => void,
} & ClippingPlane;

const PlaneToolgroup = ({
  perform, selected, id, orientation, disabled, color, rotatorsVisibility, arrowVisibility, flipped, onFlip
}: PlaneToolgroupType) => {
  const togglePlane = useCallback(() => {
    perform(Action.EDIT_CLIPPING_PLANE, { id, disabled: !disabled });
  }, [ perform, id, disabled ]);

  const toggleArrowVisibility = useCallback(() => {
    perform(Action.EDIT_CLIPPING_PLANE, { id, arrowVisibility: !arrowVisibility });
  }, [ perform, id, arrowVisibility ]);

  const toggleRotatorsVisibility = useCallback(() => {
    perform(Action.EDIT_CLIPPING_PLANE, { id, rotatorsVisibility: !rotatorsVisibility });
  }, [ perform, id, rotatorsVisibility ]);

  const deletePlane = useCallback(() => {
    perform(Action.DELETE_CLIPPING_PLANE, id);
  }, [ perform, id ]);

  const flipPlane = useCallback(() => {
    perform(Action.FLIP_CLIPPING_PLANE, id);
    onFlip(id);
  }, [ perform, id ]);

  const selectPlane = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    perform(Action.SELECT_CLIPPING_PLANE, id);
  }, [ perform, id ]);

  const focusPlane = useCallback(() => {
    perform(Action.SET_CLIPPING_PLANE_HIGHLIGHTING, { id, state: true });
  }, [ perform, id ]);

  const blurPlane = useCallback(() => {
    perform(Action.SET_CLIPPING_PLANE_HIGHLIGHTING, { id, state: false });
  }, [ perform, id ]);

  if (!selected) {
    return <UnselectedPlane
      onMouseEnter={focusPlane}
      onMouseLeave={blurPlane}
      onClick={selectPlane}
    >
      <Toolgroup>
        <PlainToolbarButton
          className='disabled'
          active={!disabled}
          onClick={selectPlane}
        >
          <PlaneIcon
            fill={`#${color}`}
            rotation={iconRotationByPlaneOrientation(orientation)}
          />
        </PlainToolbarButton>
        <PlainToolbarButton 
          className='disabled'
          active={arrowVisibility}
          onClick={selectPlane}
        >
          <MoveIcon />
        </PlainToolbarButton>
        <PlainToolbarButton 
          className='disabled'
          active={rotatorsVisibility}
          onClick={selectPlane}
        >
          <RotateIcon />
        </PlainToolbarButton>
        <PlainToolbarButton
          className='disabled'
          active={flipped}
          onClick={selectPlane}
        >
          <FlipIcon rotation={flipped ? 180 : 0}/>
        </PlainToolbarButton>
      </Toolgroup>
      <FlyingPanelCell>
        <FlyingButton
          hintDisabled={true}
          onClick={selectPlane}
        >
          <MinusIcon />
        </FlyingButton>
      </FlyingPanelCell>
    </UnselectedPlane>
  }
  return <>
    <Toolgroup>
      <ToolbarButton
        hint={`${switcherNamePrefix(!disabled)} ${t('bim.term.cut')}`}
        active={!disabled}
        onClick={togglePlane}
      >
        <PlaneIcon
          fill={`#${color}`}
          rotation={iconRotationByPlaneOrientation(orientation)}
        />
      </ToolbarButton>
      <ToolbarButton
        hint={`${switcherNamePrefix(arrowVisibility)} ${t('bim.term.shift')}`}
        active={arrowVisibility}
        onClick={toggleArrowVisibility}
      >
        <MoveIcon />
      </ToolbarButton>
      <ToolbarButton
        hint={`${switcherNamePrefix(rotatorsVisibility)} ${t('bim.term.turn')}`}
        active={rotatorsVisibility}
        onClick={toggleRotatorsVisibility}
      >
        <RotateIcon />
      </ToolbarButton>
      <ToolbarButton
        hint={t('viewer.action.flipCut')}
        active={flipped}
        onClick={flipPlane}
      >
        <FlipIcon rotation={flipped ? 180 : 0}/>
      </ToolbarButton>
    </Toolgroup>
    <FlyingPanelCell>
      <FlyingButton
        hint={t('viewer.action.deleteCut')}
        color='#DE350B'
        disabled={false}
        onClick={deletePlane}
      >
        <MinusIcon />
      </FlyingButton>
    </FlyingPanelCell>
  </>;
}

export type Props = {
  planes: ClippingPlane[],
  selected: string | null,
  clipIntersection: boolean,
  perform: (type: Action, data?: any) => void,
  disabled?: boolean,
  active?: boolean,
  indicator?: boolean,
  setEditorVisibility: (v: boolean) => void,
  setClippingVisibility: (v: boolean) => void,
  flipped: string[],
  onFlip: (id: string) => void,
};

export default ({
  disabled = false,
  active = false,
  indicator = false,
  planes,
  selected,
  clipIntersection,
  perform,
  setEditorVisibility,
  setClippingVisibility,
  flipped,
  onFlip,
}: Props) => {
  const [ hovered, setHovered ] = useState(false);
  const [ addingNew, setAddingNew ] = useState(false);
  const addPlane = useCallback((data: { orientation: ClippingPlaneOrientation }) => {
    if (!active) {
      setEditorVisibility(true);
    }

    perform(
      Action.ADD_CLIPPING_PLANE,
      {
        ...data,
        id: Date.now().toString(36),
        rotatorsVisibility: false,
        arrowVisibility: true,
      }
    );
  }, [ perform, setEditorVisibility ]);

  const toggleClipIntersection = useCallback(() => {
    perform(Action.SET_CLIP_INTERSECTION, !clipIntersection);
  }, [ perform, clipIntersection ]);

  const onMainButtonClick = useCallback(() => {
    if (!planes.length) {
      addPlane({ orientation: 'x' });
      return;
    }
    if (active) {
      setClippingVisibility(false);
      setEditorVisibility(false);
      return;
    }
    setEditorVisibility(true);
  }, [ planes.length, addPlane, active, setEditorVisibility, setClippingVisibility ]);

  const closeManager = useCallback(() => {
    setClippingVisibility(true);
    setEditorVisibility(false);
  }, [ setEditorVisibility, setClippingVisibility ]);

  const hint = useMemo(() => {
    if (!planes.length) {
      return t('viewer.action.addCut');
    }
    if (active) {
      return t('viewer.action.offCuts'); 
    }
    return t('viewer.title.cuts');
  }, [ planes.length, active ]);

  const list = useMemo(() => planes.slice().reverse(), [ planes ]);

  useEffect(() => {
    if (active && !planes.length) {
      setClippingVisibility(false);
      setEditorVisibility(false);
    }
  }, [ planes, setEditorVisibility, setClippingVisibility ]);

  return <Root>
    <ToolbarButton
      placement='right-start'
      hint={hint}
      disabled={disabled}
      active={active}
      indicator={(planes.length && !active) ? indicator : null}
      onClick={onMainButtonClick}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => !disabled && setHovered(false)}
    >
      <ClippingIcon/>
    </ToolbarButton>
    {!planes.length
      ? (hovered && (
        <FlyingPanel
          style={{ bottom: 44, paddingBottom: '12px' }}
          onMouseEnter={() => !disabled && setHovered(true)}
          onMouseLeave={() => !disabled && setHovered(false)}
        >
          <FlyingPanelCell>
            <ToolbarButton
              hint={t('viewer.action.addCut')}
              onMouseEnter={() => setAddingNew(true)}
              onMouseLeave={() => setAddingNew(false)}
            >
              <PlusIcon/>
            </ToolbarButton>
            {addingNew && <OrientationSelector
              onMouseEnter={() => setAddingNew(true)}
              onMouseLeave={() => setAddingNew(false)}
              addPlane={addPlane}
            />}
          </FlyingPanelCell>
        </FlyingPanel>
      ))
      : (active && (
        <FlyingPanel style={{ width: '268px' }}>
          <FlyingPanelCell style={{ width: '144px' }}>
            {planes.length < 5 && (<>
              <FlyingButton
                hint={t('viewer.action.addCut')}
                onMouseEnter={() => setAddingNew(true)}
                onMouseLeave={() => setAddingNew(false)}
              >
                <PlusIcon/>
              </FlyingButton>
              {addingNew && <OrientationSelector
                onMouseEnter={() => setAddingNew(true)}
                onMouseLeave={() => setAddingNew(false)}
                addPlane={addPlane}
              />}
            </>)}
          </FlyingPanelCell>
          <FlyingPanelCell>
            <FlyingButton
              color="#091E42"
              hint={clipIntersection ? t('viewer.action.cutOut') : t('viewer.action.cutIn')}
              onClick={toggleClipIntersection}
            >
              {clipIntersection
                ? <NotConjunctionIcon />
                : <ConjunctionIcon />
              }
            </FlyingButton>
          </FlyingPanelCell>
          <FlyingPanelCell>
            <FlyingButton
              hint={t('viewer.action.hideCuts')}
              color='#DE350B'
              onClick={closeManager}
            >
              <CrossIcon size={20}/>
            </FlyingButton>
          </FlyingPanelCell>
          {list.map((plane) => (
            <PlaneToolgroup
              key={plane.id}
              selected={plane.id === selected}
              perform={perform}
              flipped={flipped.includes(plane.id)}
              onFlip={onFlip}
              {...plane}
            />
          ))}
        </FlyingPanel>
      ))
    }
  </Root>;
};
